import { PlayerEventType } from 'amazon-ivs-player';
import JSZip from 'jszip';
import { memoize } from 'lodash';
import Hls from 'hls.js';
import { random } from './random';
import {
	MediaData,
	MediaDownloadLink,
	MediaItem,
	PagedMediaItemExisting,
	PresentedMediaItem,
} from '../stores/reducers/media';
import {
	AWS_ALIAS,
	DOC_TYPES,
	IMG_TYPES,
	MEDIA_UPLOAD_ERROR_MESSAGES,
	MEDIA_UPLOAD_ERROR_TYPES,
	MEDIA_UPLOAD_ERROR_DESCRIPTIONS,
	MAX_FILE_NAME_LENGTH,
	DOWNLOAD_TYPES,
	UPLOAD_STATUS,
} from '../types/constants';
import {
	CloudConvertUploadData,
	CloudConvertVideoProcessingFlags,
} from '../types/interfaces';
import {
	createUUID,
	hasSpecialCharacters,
	isVideo,
	getAssetUri,
	multiplierFibonacci,
	isValidRecordingFilename,
} from './utils';
import { showAlert } from '../components/general-components/alert-service';
import {
	MediaDimensions,
	NumberIndexObject,
} from '../stores/types/types';
import {
	GetCloudConvertJob,
	GetCloudfrontUploadUrl,
	onHttpUploadInitFunction,
	S3_SUB_DIRECTORY,
	UploadToCloudFront,
} from '../connection/media';
import getVideoThumbnailOrPoster from './video-thumbnail-generator';
import getImageThumbnail from './image-thumbnail-generator';
import { ALERT_TYPES } from '../components/general-components/alert-types';
import { ResourcePool } from '../hooks/use-resource-pool';
import { Shot, ShotFeed } from '../stores/reducers/shots';
import { appLogger } from './datadog';

// these disables are due to a bug in typescript, see the below link
// https://stackoverflow.com/questions/63961803/eslint-says-all-enums-in-typescript-app-are-already-declared-in-the-upper-scope
// eslint-disable-next-line no-shadow
export enum VideoFeedTypes {
	// eslint-disable-next-line no-unused-vars
	SingleFeed = 'SingleFeed',
	// eslint-disable-next-line no-unused-vars
	SlideDeck = 'SlideDeck',
	// eslint-disable-next-line no-unused-vars
	Hidden = 'Hidden',
	// eslint-disable-next-line no-unused-vars
	Other = 'Other',
}

interface VideoFeedTypeCount {
	feedType: VideoFeedTypes;
	videoCount: number;
}

const mp4box = require('mp4box');

export function getFileExtension(fileName: string): string {
	if (!fileName?.includes('.')) {
		return '';
	}

	let fileExtension: string | undefined = fileName.split('.').pop();

	if (fileExtension) {
		fileExtension = fileExtension.toLowerCase();
	}

	return fileExtension ?? '';
}

export function getFileExtensionGivenFile(file: File): string {
	return getFileExtension(file.name);
}

export function getTags(paramTags?: string): string {
	let tags = paramTags;

	if (!tags && !window.location.host.includes('localhost')) {
		tags = `h:${process.env.REACT_APP_DOMAIN ?? window.location.host}`;
	} else if (!window.location.host.includes('localhost')) {
		tags = `h:${process.env.REACT_APP_DOMAIN ?? window.location.host},${tags}`;
	}

	// allow for media conversion to work on a local setup
	if (!tags && process.env.REACT_APP_NGROK_DASHBOARD_API_HOST) {
		tags = `h:${process.env.REACT_APP_NGROK_DASHBOARD_API_HOST}`;
	} else if (process.env.REACT_APP_NGROK_DASHBOARD_API_HOST) {
		tags = `h:${process.env.REACT_APP_NGROK_DASHBOARD_API_HOST},${tags}`;
	}

	return tags ?? '';
}

export function createMediaFile(file: File): File {
	const extension = getFileExtensionGivenFile(file);
	const fileNameArray = file.name.split('.');
	fileNameArray.pop();
	const cleanFileName = fileNameArray.join().replace(/[`~!@#$%^&*|+=?;:"'.,<>{}[\]\\/]/gi, '').replace(/\s/g, '_');
	return new File([file], `${createUUID()}_${cleanFileName}.${extension}`);
}

export function buildMediaUrlPrefix(channel: number, meetingId: string) {
	// where .channel && .meeting_id === gr_meetings table, channel & meeting_id column
	return `uploads/${channel}/${meetingId}/${random(10)}`;
}

export function mediaUploadError(type: string, customDescription?: string) {
	showAlert({
		message: MEDIA_UPLOAD_ERROR_MESSAGES[type],
		description: customDescription || MEDIA_UPLOAD_ERROR_DESCRIPTIONS[type],
		type: type === MEDIA_UPLOAD_ERROR_TYPES.FILE_NAME
			? ALERT_TYPES.MEDIA_TITLE_ERROR
			: ALERT_TYPES.ERROR,
		duration: 10000,
	});
}

export function checkFileName(fileName: string, isRecording?: boolean, allowBlank?: boolean): boolean {
	if (allowBlank && fileName === '') return true;
	if (
		hasSpecialCharacters(fileName)
		|| (isRecording && !isValidRecordingFilename(fileName))
	) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_NAME);
		return false;
	}

	if (fileName.length === 0 || fileName.length > MAX_FILE_NAME_LENGTH) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_NAME_LENGTH);
		return false;
	}

	return true;
}

export function buildMediaItem(
	firstFile: File,
	ccUploadData: CloudConvertUploadData | null,
	grUserId: number,
	prefix: string,
	meeting: number,
): MediaItem {
	const baseMediaItem = {
		id: 1,
		created_by: grUserId,
		original_name: firstFile.name,
		created_date: new Date().getTime(),
		thumbnail_status: 1,
		location: '',
		meeting,
		type: getFileExtensionGivenFile(firstFile),
		images: '',
	};

	// TODO: this needs to be fixed, still doesn't have all the props from MediaItem
	// interface, so have to return 'as MediaItem'
	if (ccUploadData) {
		// cloudConvertUpload.endpoint is generated in the same way as below, `${AWS_ALIAS}/${prefix}`
		// but is passed into an async
		return {
			...baseMediaItem,
			endpoint: ccUploadData.endpoint,
			cloud_convert_id: ccUploadData.id,
		} as MediaItem;
	}
	// uploaded to S3, so we set the cloud_convert_id to null.
	return {
		...baseMediaItem,
		endpoint: `${AWS_ALIAS}/${prefix}`,
		cloud_convert_id: null,
	} as MediaItem;
}

export async function getMediaDimensionsGivenUrl(url: string): Promise<MediaDimensions> {
	return new Promise((resolve, reject) => {
		try {
			if (isVideo(url)) {
				const videoEl = document.createElement('video');
				let hls: Hls | null = null;

				if (url.endsWith('.m3u8')) {
					hls = new Hls();
					hls.loadSource(url);
					hls.attachMedia(videoEl);
				} else {
					videoEl.src = url;
				}

				videoEl.muted = true;

				videoEl.addEventListener('playing', () => {
					const width = videoEl.videoWidth;
					const height = videoEl.videoHeight;
					videoEl.pause();
					videoEl.remove();

					if (hls) {
						hls.destroy();
					}

					resolve({ width, height });
				});

				videoEl.play();
			} else {
				const img = new Image();
				// eslint-disable-next-line func-names
				img.addEventListener('load', function () {
					const width = this.naturalWidth;
					const height = this.naturalHeight;
					img.remove();
					resolve({ width, height });
				});
				img.src = url;
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
			reject(e);
		}
	});
}

export async function checkVideoBitrateBeforeUpload(
	file: File,
	isOptionKeyDown: boolean,
): Promise<CloudConvertVideoProcessingFlags | null> {
	return new Promise((resolve, reject) => {
		try {
			const fileExtension = getFileExtensionGivenFile(file);

			if (fileExtension !== 'mp4') {
				reject(new Error('File not mp4'));
			}

			// Check if the bitrate is greater than 4mb using mp4box library.
			// If so, we need to convert it to something lower using
			// Cloud Convert (ffmpeg). 4mb is what we broadcast out on the
			// rtmp server regardless of the actual video bitrate so no
			// reason to go above that. If mp4s are too large they play/load
			// different on slow machines/internet causing timing
			// issues in the Greenroom.
			const boxFile = mp4box.createFile();
			let boxFileState = false;

			/*  As soon as when FileReader starts to  push buffer into
			mp4box onMoovStart method will trigger
			Updating the state of fileBoxState */
			boxFile.onMoovStart = () => {
				boxFileState = true;
			};

			// called when the video file is loaded in
			boxFile.onReady = (info: any) => {
				const video = info.tracks.find((track: any) => track.type === 'video');

				// video bitrate is in bps - convert to mbps
				// The option key is an Easter egg for producers to bypass auto cloud
				// convert high bitrate mp4s. Eli
				// wanted to give them that option in case they complained
				if ((video.bitrate / 1024 / 1024) > 4 && !isOptionKeyDown) {
					// Automatically convert mp4 to lower bitrate
					resolve({ convertVideoBitrate: true, convertToWebM: false });
				} else {
					// Producer was holding option hot key. Keep mp4 bitrate. Manually set to false.
					resolve({ convertVideoBitrate: false, convertToWebM: false });
				}
			};

			// set up error handler
			boxFile.onError = (e: any) => {
				// eslint-disable-next-line no-console
				console.error(e);
				mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_READ);
				resolve(null);
			};

			// have to convert file to blob, then array buffer
			const fileReader = new FileReader();
			const blob = new Blob([file], { type: file.type });

			// when fileReader has converted the blob into an array buffer
			fileReader.onload = (e: any) => {
				// array buffer
				// array buffer
				const buffer = e.target.result;

				// this is a required prop for this package to work
				buffer.fileStart = 0;

				// push the buffer into mp4box
				boxFile.appendBuffer(buffer);
			};

			/*  fileReader.onloadend will trigger when fileReader.onload will be finished
			In other words, when mp4box completes appendBuffer method.
				- During the process if appending is happening properly
				boxFile.onMoovStart should trigger and that will
				  update the value of boxFileState. If the boxFileState value
				  is still false ( not updated ) after onload method is
				  finished then throw error msg because mp4Library was not
				  able to check-bitrate and video is not uploaded to S3.
		*/
			fileReader.onloadend = () => {
				setTimeout(() => {
					if (!boxFileState) {
						resolve(null);
						mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_READ);
					}
				}, 2500);
			};

			// read the blob
			fileReader.readAsArrayBuffer(blob);
		} catch (e) {
			resolve(null);
			mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_READ);
		}
	});
}

/**
 *
 * @param token - user token
 * @param file - the file that is being uploaded
 * @param prefix - ?
 * @param convertToWebM - is this a mov or video file that needs to be webm?
 * @param tags - comma separated tags currently supported (t:<tag> h:<tag>)
 *               note that t: means type h: hostname (hostname should never be
 *               specified outside this function as the function will take care
 *               of it
 */
export function createCloudConvertJob(
	token: string,
	file: File,
	prefix: string,
	convertToWebM?: boolean,
	paramTags?: string,
): Promise<CloudConvertUploadData> | false {
	const tags = getTags(paramTags);
	const fileExtension = getFileExtensionGivenFile(file);
	const supportedCloudConvertExtensions = ['pdf', 'pptx', 'key', 'mp4', 'mov', 'webm'];

	if (!supportedCloudConvertExtensions.some((ext: string) => ext === fileExtension)) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_TYPE);
		return false;
	}

	// .mov files with alpha channels are not supported by most web browsers
	// and need to be converted to the .webm extension
	// to be compatible with Chrome and Firefox while still preserving
	// transparent layers. This is done by using a custom ffmpeg command on
	// Cloud Convert which requires the name of the file to import and export properly.
	return GetCloudConvertJob(
		token,
		convertToWebM ? 'mov_webm' : fileExtension,
		prefix,
		file.name.split('.')[0],
		tags,
	);
}

// uploads a single image file or low-bitrate video to s3
// if upload succeeds, sets item to public via brandlive API, and returns the URL of the file
// TODO if upload fails, returns null, should handle
// onAWSHttpUploadInit is used by pass back the request data
// object to the component calling this function. Some components don't need this request.
// The media component uses the httprequest to cancel the upload by calling .abort
// onAWSUploadProgress is for media progress bars in the UI
export const uploadImageVideoToS3 = async (
	file: File,
	prefix: string,
	channel: number,
	onAWSHttpUploadInit?: onHttpUploadInitFunction,
): Promise<string | null> => {
	const key = `${prefix}/${file.name}`;
	const { url: signedUrl } = await GetCloudfrontUploadUrl(channel, key);
	const didUpload = await UploadToCloudFront(signedUrl, file, onAWSHttpUploadInit);
	if (didUpload === UPLOAD_STATUS.LOAD) {
		return `${AWS_ALIAS}/${key}`;
	}
	if (didUpload === UPLOAD_STATUS.ERROR) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
	}
	return null;
};

export const checkIfFileNeedsCloudConvert = (
	videoFileFlags: CloudConvertVideoProcessingFlags | null | undefined,
	fileExtension: string,
) => {
	if (DOC_TYPES.some((type: string) => type === fileExtension)) {
		return true;
	} if (IMG_TYPES.some((type: string) => type === fileExtension)) {
		return false;
	} if (videoFileFlags) {
		if (videoFileFlags.convertToWebM || videoFileFlags.convertVideoBitrate) {
			return true;
		}
		return false;
	}
	return false;
};

export const createThumbnailOrPoster = async (
	file: File,
	prefix: string,
	isPoster: boolean,
	channel: number,
// eslint-disable-next-line no-async-promise-executor
) => new Promise(async (resolve) => {
	try {
		const fileExtension = getFileExtensionGivenFile(file);
		const image = fileExtension === 'mp4' || fileExtension === 'webm' ? await getVideoThumbnailOrPoster(file, isPoster) as Blob : await getImageThumbnail(file) as Blob;
		// Whenever thumb is false, it will throw out an error message and clear/remove the media data
		if (!image) {
			mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
			resolve(false);
		} else {
			const imageName = file.name.replace(/(.mp4|.mov|.webm|.jpeg|.gif|.png)$/, fileExtension === 'webm' ? '.png' : '.jpg');
			const subdirectory = isPoster ? S3_SUB_DIRECTORY.POSTER : S3_SUB_DIRECTORY.THUMBS;
			const key = `${prefix}/${subdirectory}/${imageName}`;
			const { url: signedUrl } = await GetCloudfrontUploadUrl(channel, key);
			await UploadToCloudFront(signedUrl, image);
			resolve(true);
		}
	} catch (e) {
		mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
		resolve(false);
	}
});

// WebM files created with the MediaRecorder may not have a properly set duration (Infinity or NaN).
// We can fix this by creating a copy of the file with FFmpeg using Cloud Convert.
export const checkWebMDuration = async (
	file: File,
): Promise<CloudConvertVideoProcessingFlags | null> => new Promise((resolve, reject) => {
	try {
		const url = URL.createObjectURL(file);
		const videoEl: HTMLMediaElement = document.createElement('video');
		videoEl.src = url;
		videoEl.muted = true;

		videoEl.addEventListener('playing', () => {
			const invalidDuration = !!(videoEl.duration === Infinity || Number.isNaN(videoEl.duration));

			resolve({
				convertVideoBitrate: invalidDuration,
				convertToWebM: false,
			});
		});

		videoEl.onerror = () => {
			resolve(null);
		};

		videoEl.play();
	} catch (e) {
		reject(e);
	}
});

export const isValidImage = async (
	uri: string,
	paramImgElement: HTMLImageElement,
) => new Promise((resolve) => {
	const imgElement = paramImgElement;
	imgElement.onload = () => resolve(true);
	imgElement.onerror = () => resolve(false);
	imgElement.src = uri.includes('https:') ? uri : getAssetUri(uri);
});

export const getVideoDuration = memoize(async (
	mediaUrl: string,
	mediaType: string,
	paramVideo: HTMLVideoElement,
	testPlayer?: ResourcePool,
) => {
	const video = paramVideo;
	const returnedDuration:number = await new Promise<number>((resolve, reject) => {
		try {
			if ((mediaType === 'hls' || mediaUrl.endsWith('.m3u8')) && testPlayer) {
				const playerContainer = testPlayer.getElement();
				const player = playerContainer.data;

				player.attachHTMLVideoElement(video);

				let duration: any;
				let bufferAssumption = 1; // This is in seconds
				let playInterval: NodeJS.Timeout;
				let retryInterval = 100;

				const playAfterSeek = () => {
					// after we seek we want to play
					// in order for buffering to start
					player.addEventListener(
						PlayerEventType.BUFFER_UPDATE,
						// eslint-disable-next-line @typescript-eslint/no-use-before-define
						bufferUpdateResolve,
					);

					if (playInterval) {
						clearInterval(playInterval);
					}

					// sometimes play does not trigger buffer so we retry
					playInterval = setInterval(() => {
						retryInterval = multiplierFibonacci(retryInterval, 100);

						if (player.isPaused()) {
							player.play();
						} else {
							player.pause();
							bufferAssumption += 1;
							player.seekTo(duration - bufferAssumption);
						}
					}, retryInterval);
				};

				const durationChanged = () => {
					duration = player.getDuration() || 0;
					player.addEventListener(
						PlayerEventType.SEEK_COMPLETED,
						playAfterSeek,
					);
					player.removeEventListener(
						PlayerEventType.DURATION_CHANGED,
						durationChanged,
					);
					// We seek to near the end to prepare to buffer
					// since buffering will give us the true value of the duration.
					player.seekTo(duration - bufferAssumption);
				};

				const bufferUpdateResolve = () => {
					if (playInterval) {
						clearInterval(playInterval);
					}

					player.pause();

					// We have the buffer duration to compare against
					player.removeEventListener(
						PlayerEventType.SEEK_COMPLETED,
						playAfterSeek,
					);
					player.removeEventListener(
						PlayerEventType.DURATION_CHANGED,
						durationChanged,
					);
					player.removeEventListener(
						PlayerEventType.BUFFER_UPDATE,
						bufferUpdateResolve,
					);

					const bufferDuration = player.getBufferDuration();

					/*
						Checking for undefined this way for clarity and since we
						don't want it to fail for the falsy value
						`bufferDuration === 0` - but we also don't want to let
						negative numbers through.
					*/
					if (bufferDuration !== undefined && bufferDuration >= 0) {
						testPlayer.releaseElement(playerContainer.data.core.id);
						resolve(duration - (bufferAssumption - bufferDuration));
					} else {
						// player.getBufferDuration() gave undefined so we need to seek backward more
						bufferAssumption += 1;
						player.addEventListener(
							PlayerEventType.SEEK_COMPLETED,
							playAfterSeek,
						);

						player.seekTo(duration - bufferAssumption);
					}
				};

				player.addEventListener(
					PlayerEventType.DURATION_CHANGED,
					durationChanged,
				);

				// don't want to accidentally play audio
				player.setVolume(0);
				player.load(mediaUrl);
			} else {
				video.onloadedmetadata = () => {
					const duration = video.duration || 0;
					window.URL.revokeObjectURL(video.src);
					resolve(duration);
				};

				video.src = mediaUrl;
			}
		} catch (e) {
			// weird interaction where e can't be Error type
			appLogger.warn('getVideoDuration failed', { error: e });
			reject(e);
		}
	});

	return returnedDuration;
});

export const pageExists = (
	// indexed to 1
	newPage: number,
	item: MediaItem,
): boolean | string => {
	const page = item.media_data.image_array[newPage - 1];
	return page ?? false;
};

export const downloadFile = async (
	url: string,
	name: string,
	directDownload?: boolean,
) => {
	const extension = getFileExtension(url.split('/').pop()!);
	const fileName = !extension || getFileExtension(name) === extension
		? name
		: `${name}.${extension}`;
	const link = document.createElement('a');
	const iframeId = 'greenroom-download-iframe';
	const safeFileName = encodeURIComponent(fileName);
  	const safeUrl = encodeURI(url);

	let iframe = document.getElementById(iframeId);

	if (!iframe) {
		const newIframe = document.createElement('iframe');

		newIframe.src = 'about:blank';
		newIframe.id = iframeId;
		newIframe.name = iframeId;
		newIframe.style.display = 'none';
		newIframe.style.visibility = 'hidden';
		iframe = newIframe;
		document.body.appendChild(iframe);
	}

	if (!directDownload) {
		const fileResponse = await fetch(safeUrl, { mode: 'cors' });

		if (!fileResponse || fileResponse.status !== 200 || !fileResponse.blob) {
			return;
		}

		const blobResponse = await fileResponse.blob();

		if (!blobResponse) {
			return;
		}

		link.href = URL.createObjectURL(blobResponse);
	} else {
		link.href = `${safeUrl}?response-content-disposition=attachment;filename=${safeFileName}`;
	}

	link.target = iframeId;
	link.download = fileName;
	link.style.display = 'none';
	link.style.visibility = 'hidden';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const downloadMedia = async (
	media: MediaItem,
	downloadType: string,
	preCallback?: Function,
	postCallback?: Function,
	mediaDownloadLink?: MediaDownloadLink,
) => {
	preCallback?.();

	if (!media.download_links || media.download_links.length === 0 || !media.download_name) {
		setTimeout(() => {
			postCallback?.();
		}, 500);
		return;
	}

	const filesToDownload = media.download_links
		.filter((downloadLink) => downloadLink.type === downloadType);

	switch (downloadType) {
		case DOWNLOAD_TYPES.FILE: {
			if (filesToDownload.length > 1) {
				const zip = new JSZip();
				const folder = zip.folder(`${media.download_name}`);
				const avoidCache = `?${Date.now().toString()}`;
				const blobPromises: Promise<any>[] = [];

				filesToDownload.forEach((fileToDownload) => {
					const fetchUrl = fileToDownload.url + avoidCache;

					blobPromises.push(fetch(fetchUrl).then((r) => {
						if (r.status === 200) {
							const name = fileToDownload.url.substring(fileToDownload.url.lastIndexOf('/'));
							folder?.file(name, r.blob());
						}
					}));
				});

				await Promise.all(blobPromises);

				const zipBlob = await zip.generateAsync({ type: 'blob' });

				filesToDownload[0].url = URL.createObjectURL(zipBlob);
				// eslint-disable-next-line no-param-reassign
				media.download_name = `${media.download_name}.zip`;
			}

			await downloadFile(
				filesToDownload[0].url,
				media.download_name,
			);

			break;
		}
		case DOWNLOAD_TYPES.RECORDING: {
			await downloadFile(
				filesToDownload[0].url,
				media.download_name,
				true,
			);

			break;
		}
		case DOWNLOAD_TYPES.SINGLE_ISO: {
			if (mediaDownloadLink === undefined) {
				break;
			}

			const {
				url,
				name,
				order,
			} = mediaDownloadLink;

			await downloadFile(
				url,
				`${name}${order && order > 1 ? ` ${order}` : ''}`,
				true,
			);
			break;
		}
		default: {
			break;
		}
	}

	setTimeout(() => {
		postCallback?.();
	}, 500);
};

export const getShotFeedVideoInfo = (
	mediaId: number,
	mediaArray: MediaItem[],
	isHidden: boolean,
): VideoFeedTypeCount => {
	const mediaItem = mediaArray.find(({ id }) => id === mediaId);

	if (mediaItem) {
		const {
			media_data: {
				image_array: imageArray,
			},
		} = mediaItem;
		const mediaItemUrl = imageArray[0];

		// If this is a video not in a slide deck
		if (
			imageArray.length === 1
			&& mediaItemUrl
			&& isVideo(mediaItemUrl)
		) {
			// If this is a hidden / audio only feed or not
			if (isHidden) {
				return {
					feedType: VideoFeedTypes.Hidden,
					videoCount: 1,
				};
			}

			return {
				feedType: VideoFeedTypes.SingleFeed,
				videoCount: 1,
			};
		}

		// This must be a slide deck
		const slideDeckVideoCount = imageArray.reduce((
			imageArrayVideoCount: number,
			imageUrl: string,
		) => {
			if (isVideo(imageUrl)) {
				return imageArrayVideoCount + 1;
			}

			return imageArrayVideoCount;
		}, 0);

		return {
			feedType: VideoFeedTypes.SlideDeck,
			videoCount: slideDeckVideoCount,
		};
	}

	return {
		feedType: VideoFeedTypes.Other,
		videoCount: 0,
	};
};

export const getPresentedAutoDurationMediaId = (
	currentShot: Shot,
	mediaArray: MediaItem[],
	presentedMediaArray: PresentedMediaItem[],
): number | null => {
	const { shotFeeds } = currentShot.shot_data;

	/*
		Seems like a quick way to look up AOF status
		(without looping through each time)
		-
		const aofHashMap = {
			[mediaId1]: isAofBool,
		};
	*/
	const aofHashMap: NumberIndexObject<boolean> = shotFeeds.reduce((
		hashMap: NumberIndexObject<boolean>,
		{
			media,
			audioOnly,
		}: ShotFeed,
	) => {
		if (media) {
			return {
				...hashMap,
				[media]: !!audioOnly,
			};
		}

		return hashMap;
	}, {});

	let getMediaId = 0;
	let singlePageVideoCount = 0;

	for (let index = 0; index < presentedMediaArray.length; index += 1) {
		const {
			mediaId,
		} = presentedMediaArray[index];
		const matchingMedia = mediaArray.find(({ id }) => id === mediaId);
		const imageArray = matchingMedia?.media_data.image_array;
		const isAof = aofHashMap[mediaId];

		if (
			imageArray?.length === 1
			&& isVideo(imageArray[0])
			&& !isAof
			&& singlePageVideoCount < 2
		) {
			getMediaId = mediaId;
			singlePageVideoCount += 1;

			if (singlePageVideoCount > 1) {
				return null;
			}
		}
	}

	if (getMediaId && singlePageVideoCount === 1) {
		return getMediaId;
	}

	return null;
};

export const getPresentedAutoDurationTimeLeft = (
	feedMediaId: number,
	currentShot: Shot,
	presentedMediaArray: PresentedMediaItem[],
): number => {
	const currentPresentedMedia = presentedMediaArray.find(
		({ mediaId }) => mediaId === feedMediaId,
	);
	const { shotDuration } = currentShot.shot_data;
	const timeElapsed = currentPresentedMedia?.videoState?.timeElapsed;
	const timeLeft = timeElapsed
		? shotDuration - timeElapsed
		: shotDuration;

	if (timeLeft > 0) {
		return timeLeft;
	}

	return 0;
};

// page indexed at 1
export function getCurrentPageThumb(
	data: MediaData = { thumbnail_map: {}, image_array: [''] },
	page: number = 1,
): string {
	const { thumbnail_map: thumbs, image_array: images } = data;
	const currentPage = page ?? 1;
	const image = images[currentPage - 1] ?? '';
	return thumbs[image] ?? image ?? '';
}

export function uploadMediaToApi(
	token: string,
	meeting: number,
	file: FileList,
	onHttpUploadInit?: onHttpUploadInitFunction,
) {
	return new Promise((resolve) => { // no reject
		try {
			const url = `${process.env.REACT_APP_API_HOST}/v2/greenroom/media/${meeting}/upload`;

			const data = new FormData();
			data.append('file', file[0], file[0].name);

			const request = new XMLHttpRequest();
			onHttpUploadInit?.(request);
			request.open('POST', url);
			request.setRequestHeader('bl-token', token);
			request.onload = () => resolve(UPLOAD_STATUS.LOAD);
			request.onabort = () => resolve(UPLOAD_STATUS.ABORT);
			request.onerror = () => resolve(UPLOAD_STATUS.ERROR);
			request.send(data);
		} catch (error) {
			console.error('error => ', error);
			resolve(false);
		}
	});
}

/**
 * Resize image using maximum width and height constraints while conserving aspect ratio.
 * @param {File} file - The file that is being uploaded.
 * @param {string} extension - The file's extension.
 * @param {number} maxWidth - Maximum width value for returned image in pixels.
 * @param {number} maxHeight - Maximum height value for returned image in pixels.
 * @returns {Promise} - The original file or the file with adjusted width and height.
 */
export function resizeImage(
	file: File,
	extension: string,
	maxWidth: number,
	maxHeight: number,
): Promise<File> {
	return new Promise((resolve, reject) => {
		try {
			const canvas = document.createElement('canvas');
			const imageObj = new Image();
			const fileType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;

			imageObj.onload = () => {
				const resizeRatio = Math.min(maxWidth / imageObj.width, maxHeight / imageObj.height);

				if (resizeRatio >= 1) {
					// image is smaller than both max constraints if resize ratio is 1 or greater
					resolve(file);
				}

				const newWidth = imageObj.width * resizeRatio;
				const newHeight = imageObj.height * resizeRatio;
				canvas.width = newWidth;
				canvas.height = newHeight;
				const ctx = canvas.getContext('2d');

				if (!ctx) {
					resolve(file);
				} else {
					ctx.drawImage(imageObj, 0, 0, newWidth, newHeight);
					canvas.toBlob((blob) => {
						const fileReturn = blob
							? new File([blob], file.name, { type: fileType })
							: file;
						resolve(fileReturn);
					}, fileType);
				}
			};

			const url = URL.createObjectURL(file);
			imageObj.src = url;
		} catch (e) {
			reject(e);
		}
	});
}

export const getVideoStill = (url: string, type: string) => {
	if (!url || !isVideo(url)) return url;

	let still = '';
	if (url.endsWith('-hls/master.m3u8')) {
		still = url.replace(/(-mov-hls\/master.m3u8|-mp4-hls\/master.m3u8|-hls\/master.m3u8)$/, '.jpg');
	} else {
		still = url.endsWith('.webm') ? url.replace(/(.webm)$/, '.png') : url?.replace(/(.mp4|.mov)$/, '.jpg');
	}

	still = still.split('/').map((item: string, index: number, arr: string[]) => {
		if (index === arr.length - 1) {
			return `${type}/${item}`;
		}
		return item;
	}).join('/');
	return still ?? undefined;
};

/**
 * Finds a media page from the mediaPages array.
 * Tries to match by mediaId first (if provided),
 * then falls back to matching by URL.
 * @param mediaPages - Array of existing media pages
 * @param targetURL - URL to search for
 * @param mediaId - Optional mediaId to match first
 * @returns The matching media page or null
 */
export const findMediaPageBy = (
	mediaPages: PagedMediaItemExisting[] | undefined,
	targetURL: string | null,
	mediaId?: number,
): PagedMediaItemExisting | null => {
	if (mediaId) {
		const byMediaId = mediaPages?.find(x => x.mediaId === mediaId);
		if (byMediaId) {
			return byMediaId;
		}
	}

	if (!targetURL) {
		return null;
	}
	return mediaPages?.find(({ url }) => url === targetURL) ?? null;
};
